import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Autocomplete,
  Card,
  CardContent,
  InputAdornment,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import KitchenIcon from '@mui/icons-material/Kitchen';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { useNavigate } from 'react-router-dom';
import { useGetLocationsQuery, useCreateProductionMutation } from '../../../store/api/inventoryApi';
import { useGetBomsQuery, useGetBomQuery } from '../../../store/api/bomApi';
import { useGetProductsQuery } from '../../../store/api/productsApi';

function ProductionForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    productId: '',
    bomId: '',
    locationId: '',
    quantity: 1,
    batchCount: 1,
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const { data: locationsData } = useGetLocationsQuery();
  const { data: bomsData, isLoading: loadingBoms } = useGetBomsQuery({ 
    limit: 1000,
    locationId: formData.locationId || undefined 
  });
  const [createProduction, { isLoading: isSaving }] = useCreateProductionMutation();
  const { data: productsData } = useGetProductsQuery({ limit: 1000, productType: 'finished' });
  
  // Deep-fetch the selected BOM to get calculated sub-assembly costs
  const { data: fullBomData, isFetching: loadingFullBom } = useGetBomQuery(formData.bomId, { 
    skip: !formData.bomId 
  });

  const [selectedBom, setSelectedBom] = useState(null);
  const [error, setError] = useState(null);

  const bomsList = bomsData?.boms || [];
  const finishedGoodsList = productsData?.products || [];

  useEffect(() => {
    if (formData.bomId && fullBomData) {
      setSelectedBom(fullBomData);
      
      // When recipe changes, reset quantity based on yield
      const newQty = (parseFloat(fullBomData.yield) || 1) * formData.batchCount;
      setFormData((prev) => ({
        ...prev,
        quantity: newQty,
      }));
    } else {
      setSelectedBom(null);
    }
  }, [formData.bomId, fullBomData]);

  const handleBatchChange = (val) => {
    const batches = parseFloat(val) || 0;
    const yieldValue = parseFloat(selectedBom?.yield) || 1;
    setFormData({
      ...formData,
      batchCount: val,
      quantity: (batches * yieldValue).toFixed(2)
    });
  };

  const handleQuantityChange = (val) => {
    const qty = parseFloat(val) || 0;
    const yieldValue = parseFloat(selectedBom?.yield) || 1;
    setFormData({
      ...formData,
      quantity: val,
      batchCount: (qty / yieldValue).toFixed(2)
    });
  };

  const calculateIngredientTotal = () => {
    if (!selectedBom) return 0;
    const yieldValue = parseFloat(selectedBom.yield) || 1;
    return (selectedBom.items || []).reduce((sum, item) => {
      const cost = parseFloat(item.ingredient?.costPrice || 0);
      const qty = (parseFloat(item.quantity || 0) / yieldValue) * parseFloat(formData.quantity || 1);
      return sum + cost * qty;
    }, 0);
  };
  
  // Auto-select BOM if only one exists for the selected product and location
  useEffect(() => {
    if (formData.productId && bomsData?.boms) {
      const matchingBoms = bomsData.boms.filter(b => b.productId === formData.productId);
      // Auto-select if there's exactly one recipe for this product in this location
      if (matchingBoms.length === 1 && formData.bomId !== matchingBoms[0].id) {
        setFormData(prev => ({
          ...prev,
          bomId: matchingBoms[0].id
        }));
      }
    }
  }, [formData.productId, formData.locationId, bomsData, formData.bomId]);

  const handleSubmit = async () => {
    if (!formData.bomId) {
      setError('Please select a Recipe');
      return;
    }
    if (!formData.locationId) {
      setError('Please select a Kitchen Location');
      return;
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setError('Please enter a valid positive quantity to produce');
      return;
    }

    try {
      await createProduction({
        bomId: formData.bomId,
        locationId: formData.locationId,
        quantity: parseFloat(formData.quantity),
        date: formData.date,
        notes: formData.notes,
      }).unwrap();
      navigate('/inventory/productions');
    } catch (err) {
      setError(err?.data?.message || 'Failed to record production batch');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/inventory/productions')} sx={{ color: '#E65100' }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
          New Production Run
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Main Selection Card */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', mb: 3 }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#34495E', display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <RestaurantMenuIcon color="primary" /> Recipe & Output Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={finishedGoodsList}
                    getOptionLabel={(option) => `${option.name} (${option.sku})`}
                    value={finishedGoodsList.find((p) => p.id === formData.productId) || null}
                    onChange={(e, value) => setFormData({ ...formData, productId: value?.id || '', bomId: '' })}
                    renderInput={(params) => (
                      <TextField {...params} label="Item to Produce" required placeholder="Select Product..." />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    options={locationsData || []}
                    getOptionLabel={(option) => option.name}
                    value={(locationsData || []).find((l) => l.id === formData.locationId) || null}
                    onChange={(e, value) => setFormData({ ...formData, locationId: value?.id || '' })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Kitchen / Production Location"
                        required
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <KitchenIcon sx={{ color: '#E65100' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={bomsList.filter(b => !formData.productId || b.productId === formData.productId)}
                    getOptionLabel={(option) => `${option.name} ${option.location ? `(${option.location.name})` : '(Global)'}`}
                    loading={loadingBoms}
                    value={bomsList.find((b) => b.id === formData.bomId) || null}
                    onChange={(e, value) => setFormData({ ...formData, bomId: value?.id || '' })}
                    renderInput={(params) => (
                      <TextField {...params} label="Select Recipe (BOM)" required placeholder="e.g. Standard Recipe" />
                    )}
                  />
                  {selectedBom && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'primary.main', fontWeight: 600 }}>
                      Recipe Yield: {selectedBom.yield} {selectedBom.product?.baseUnit || 'units'} per Batch
                    </Typography>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Batches"
                    helperText="How many times to run this recipe?"
                    value={formData.batchCount}
                    onChange={(e) => handleBatchChange(e.target.value)}
                    disabled={!selectedBom}
                    inputProps={{ min: 0.01, step: 1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Total Quantity to Produce"
                    helperText={`Calculated output (${selectedBom?.product?.baseUnit || 'units'})`}
                    value={formData.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    disabled={!selectedBom}
                    required
                    inputProps={{ min: 0.01, step: 0.1 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Production Date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    label="Batch / Reference Notes"
                    placeholder="e.g. Morning Shift Batch 1"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Box>
          </Card>

          {/* Ingredient Preview Table */}
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#34495E', mb: 2 }}>
                Estimated Ingredient Deductions
              </Typography>
              <TableContainer sx={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#F8F9FA' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Raw Material</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Recipe Unit Qty</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Batch Required Qty</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Unit Cost</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!selectedBom ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          Select a recipe above to preview raw ingredient deductions.
                        </TableCell>
                      </TableRow>
                    ) : (selectedBom.items || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          No ingredients mapped to this recipe.
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedBom.items.map((item, idx) => {
                        const yieldValue = parseFloat(selectedBom.yield) || 1;
                        const recipeQty = parseFloat(item.quantity || 0);
                        const batchQty = (recipeQty / yieldValue) * parseFloat(formData.quantity || 1);
                        const costPrice = parseFloat(item.ingredient?.costPrice || 0);
                        const subtotal = batchQty * costPrice;

                        return (
                          <TableRow key={idx} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{item.ingredient?.name}</TableCell>
                            <TableCell>{item.ingredient?.sku}</TableCell>
                            <TableCell align="right">{recipeQty} {item.unitOfMeasure}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, color: '#E65100' }}>
                              {batchQty.toFixed(3)} {item.unitOfMeasure}
                            </TableCell>
                            <TableCell align="right">
                              {costPrice.toFixed(2)} AED
                              {item.ingredient?.productType === 'finished' && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontSize: '0.65rem', fontWeight: 700 }}>
                                  FROM RECIPE
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{subtotal.toFixed(2)} AED</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Card>
        </Grid>

        {/* Costing Summary Card */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', height: '100%' }}>
            <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2C3E50', mb: 3 }}>
                  Production Summary
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body1" color="text.secondary">Produced Item:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#2C3E50' }}>
                    {selectedBom?.product?.name || '—'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body1" color="text.secondary">Quantity Output:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#E65100' }}>
                    {formData.quantity} unit(s)
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body1" color="text.secondary">Total Ingredients Used:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedBom?.items?.length || 0} items
                  </Typography>
                </Box>

                <Box sx={{ p: 2, mt: 4, borderRadius: 2, backgroundColor: '#FFF3E0', border: '1px solid #FFE0B2', textAlign: 'center' }}>
                  <MonetizationOnIcon sx={{ fontSize: 40, color: '#E65100', mb: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">Estimated Recipe Preparation Cost</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#E65100', mt: 0.5 }}>
                    {calculateIngredientTotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
                  </Typography>
                  <Divider sx={{ my: 1, borderColor: '#FFE0B2' }} />
                  <Typography variant="subtitle2" color="text.secondary">Unit Preparation Cost</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#2C3E50', mt: 0.5 }}>
                    {(calculateIngredientTotal() / (parseFloat(formData.quantity) || 1)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AED
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  disabled={isSaving}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
                    },
                  }}
                >
                  {isSaving ? 'Processing Production...' : 'Submit Production Run'}
                </Button>
                <Button variant="outlined" fullWidth onClick={() => navigate('/inventory/productions')} sx={{ py: 1.5, borderRadius: 2 }}>
                  Cancel
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProductionForm;
